import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Paper,
  FormControl,
  InputLabel,
  Container,
  Card,
  CardContent,
  Avatar,
  Divider,
  Alert,
  Button,
  ButtonGroup,
  CircularProgress
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import CustomTreeView from '../CustomTreeView/CustomTreeView';
import { getPages, getPermisisons, getUsersExceptLogin, postRequest } from '../../api/axios';
import { getPermissions, getUsername } from '../../context/AuthFunc';
import { PERMISSION_ENDPOINT } from '../../constants/const';
import { useTranslation } from 'react-i18next';

const AccessRights = () => {
  const { t } = useTranslation('global');

  const [users, setUsers] = useState([]);
  const [pages, setPages] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [errMsg, setErrMsg] = useState('');
  const [succ, setSucc] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [applyingPreset, setApplyingPreset] = useState(false);

  const extractPageIdsFromToken = (permissions) => {
    return permissions.map(permission => parseInt(permission.split('_')[0]));
  };

  const buildTreeData = (pages) => {
    // Group pages into logical categories
    const categories = {
      home: { id: 'home', label: t("navbar.home"), children: [] },
      tickets: { id: 'tickets', label: t("navbar.tickets"), children: [] },
      reports: { id: 'reports', label: t("navbar.reports"), children: [] },
      admin: { id: 'admin', label: t("navbar.admin"), children: [] },
      sales: { id: 'sales', label: t("navbar.sales"), children: [] },
      fiscalization: { id: 'fiscalization', label: t("navbar.fiscalization"), children: [] },
      canteen: { id: 'canteen', label: t("navbar.canteen"), children: [] },
      settings: { id: 'settings', label: t("navbar.Settings"), children: [] },
      navbar: { id: 'navbar', label: t("navbar.hik"), children: [] }
    };

    // Map pages to categories
    pages.forEach(page => {
      if (page.path === '/home' || page.path === '/ticket-info' || page.path === '/ticket/edit' || page.path === '/search-users') {
        categories.home.children.push({ id: page.id, label: t(`access.${page.id}`) });
      } else if (['/active', '/passed', '/nonactive', '/sold'].includes(page.path)) {
        categories.tickets.children.push({ id: page.id, label: t(`access.${page.id}`) });
      } else if (['/category', '/kind', '/type', '/dailyrep', '/log_card'].includes(page.path)) {
        categories.reports.children.push({ id: page.id, label: t(`access.${page.id}`) });
      } else if (['/template-groups', '/template-group-events', '/ticket-templates', '/ticket-design', '/new-reader', '/readers', '/readers/edit', '/new-user', '/access', '/readers', '/logs'].includes(page.path)) {
        categories.admin.children.push({ id: page.id, label: t(`access.${page.id}`) });
      } else if (['/new-kind', '/new-category', '/new-type', '/new-ticket', '/new-item', '/price-list/edit', '/price-list/delete', '/price-list', '/monitoring', '/purchasing', '/payment_method'].includes(page.path)) {
        categories.sales.children.push({ id: page.id, label: t(`access.${page.id}`) });
      } else if (['/settings', '/update-database'].includes(page.path)) {
        categories.settings.children.push({ id: page.id, label: t(`access.${page.id}`) });
      } else if (['/refund', '/fiscalization-reports'].includes(page.path)) {
        categories.fiscalization.children.push({ id: page.id, label: t(`access.${page.id}`) });
      } else if (['/new-employee', '/new-organization-unit', '/new-article', '/new-unit-of-measure', '/new-ingredient'].includes(page.path)) {
        categories.canteen.children.push({ id: page.id, label: t(`access.${page.id}`) });
      } else if ((['/hik']).includes(page.path)) {
        categories.navbar.children.push({ id: page.id, label: t(`access.${page.id}`) });
      }
    });

    return Object.values(categories);
  };

  const getCheckedItems = (permissions) => {
    return permissions.map(p => p.page_id.toString());
  };

  // Extract all unique templates from pages
  const getAllTemplates = () => {
    const templateSet = new Set();

    pages.forEach(page => {
      if (page.templates) {
        const templates = page.templates.split(',').map(t => t.trim()).filter(t => t);
        templates.forEach(template => {
          templateSet.add(template);
        });
      }
    });

    return Array.from(templateSet).sort();
  };

  // Helper function to check if a page's templates includes a specific template
  const pageHasTemplate = (page, templateName) => {
    if (!page.templates) return false;
    const templates = page.templates.split(',').map(t => t.trim().toLowerCase());
    return templates.includes(templateName.toLowerCase());
  };

  // Define permission presets based on templates column from pages
  const getPresetPermissions = (presetName) => {
    if (!pages.length) return [];

    // Custom preset returns empty (user will manually select)
    if (presetName === 'custom') {
      return [];
    }

    // Super-admin gets all pages
    if (presetName === 'super-admin') {
      return pages.map(page => page.id.toString());
    }

    // For any template preset, get pages that have that template
    return pages
      .filter(page => pageHasTemplate(page, presetName))
      .map(page => page.id.toString());
  };

  // Generate presets dynamically from templates found in pages
  const generatePresets = () => {
    const templates = getAllTemplates();

    // Always include super-admin as the first preset
    const presetsList = [
      {
        id: 'super-admin',
        label: 'Super Admin'
      }
    ];

    // Add other template presets (excluding super-admin if it exists in templates)
    const otherTemplates = templates.filter(t => t !== 'super-admin');
    const templatePresets = otherTemplates.map(template => ({
      id: template,
      label: template.charAt(0).toUpperCase() + template.slice(1).replace(/-/g, ' ')
    }));

    // Add custom preset at the end
    return [
      ...presetsList,
      ...templatePresets,
      {
        id: 'custom',
        label: t("authorization.presetCustom") || "Prilagođeno"
      }
    ];
  };

  const applyPreset = async (presetName) => {
    if (!selectedUser) {
      setErrMsg(t("authorization.selectUserFirst") || "Please select a user first");
      return;
    }

    // Don't apply permissions for custom preset
    if (presetName === 'custom') {
      setSelectedPreset('custom');
      setSucc(t("authorization.customMode") || "Prilagođeni režim - ručno podešavanje");
      return;
    }

    setApplyingPreset(true);
    setErrMsg('');
    setSucc('');

    try {
      const presetPageIds = getPresetPermissions(presetName);
      const currentPageIds = permissions.map(p => p.page_id.toString());

      // Find pages to remove and add
      const pagesToRemove = permissions
        .filter(perm => !presetPageIds.includes(perm.page_id.toString()))
        .map(perm => perm.page_id);

      const pagesToAdd = presetPageIds
        .filter(pageId => !currentPageIds.includes(pageId))
        .map(pageId => parseInt(pageId));

      // Execute all permission changes in parallel batches
      const removePromises = pagesToRemove.map(pageId =>
        handlePermissionChange(pageId, 'view', false)
      );

      const addPromises = pagesToAdd.map(pageId =>
        handlePermissionChange(pageId, 'view', true)
      );

      // Wait for all changes to complete
      await Promise.all([...removePromises, ...addPromises]);

      // Small delay to ensure backend processes all changes
      await new Promise(resolve => setTimeout(resolve, 500));

      // Refresh permissions
      await fetchUserPermissions(selectedUser.id);
      setSelectedPreset(presetName);
      setSucc(t("authorization.presetApplied") || `Preset "${presetName}" successfully applied`);
    } catch (error) {
      setErrMsg(t("authorization.presetError") || "Error applying preset");
      console.error(error);
    } finally {
      setApplyingPreset(false);
    }
  };

  // Generate presets dynamically based on templates found in pages
  // Recalculate when pages change
  const presets = useMemo(() => generatePresets(), [pages, t]);


  useEffect(() => {
    fetchUsers();
    setUserPermissions(getPermissions());
  }, []);

  useEffect(() => {
    fetchPages();
    if (selectedUser) {
      fetchUserPermissions(selectedUser.id);
      setSelectedPreset(null); // Reset preset when user changes
    }
  }, [selectedUser]);

  const fetchUsers = async () => {
    const username = getUsername();
    const response = await getUsersExceptLogin(username);
    setUsers(response);
  };

  const fetchPages = async () => {
    const response = await getPages();
    const allowedPageIds = extractPageIdsFromToken(userPermissions);
    const filteredPages = response.filter(page => allowedPageIds.includes(parseInt(page.id)));
    setPages(filteredPages);
  };

  const fetchUserPermissions = async (user_id) => {
    const response = await getPermisisons(user_id);
    setPermissions(response);
  };

  const handlePermissionChange = async (pageId, permission, isChecked) => {
    const action = isChecked ? 'add' : 'remove';

    const formData = new URLSearchParams();
    formData.append('user_id', selectedUser.id);
    formData.append('page_id', pageId);
    formData.append('action', action);

    const response = await postRequest(PERMISSION_ENDPOINT, formData);

    if (response.success) {
      // Don't refresh permissions here when applying preset (will be done in batch)
      if (!applyingPreset) {
        fetchUserPermissions(selectedUser.id);
        setSucc(t("authorization.success"));
        setErrMsg('');
      }
      return true;
    } else {
      if (!applyingPreset) {
        setErrMsg(t("authorization.unsuccessful"));
      }
      return false;
    }
  };

  const handleUserChange = (e) => {
    const selectedUserId = e.target.value;
    const user = users.find(user => user.id === selectedUserId);
    if (user) {
      setSelectedUser(user);
    } else {
      console.error(t("authorization.selectedUserNotFound"));
    }
  };

  const treeData = buildTreeData(pages);
  const checkedItems = getCheckedItems(permissions);

  const handleTreeCheck = async (nodeId, isChecked) => {
    // Handle parent nodes
    const parentCategory = treeData.find(cat => cat.id === nodeId);
    if (parentCategory) {
      // Check/uncheck all children
      for (const child of parentCategory.children) {
        await handlePermissionChange(child.id, 'view', isChecked);
      }
      return;
    }

    // Handle leaf nodes
    await handlePermissionChange(nodeId, 'view', isChecked);

    // Get fresh permissions after update
    const updatedPermissions = await getPermisisons(selectedUser.id);

    // Find parent of this node and check if all siblings are checked
    for (const category of treeData) {
      const childNode = category.children.find(child => child.id === nodeId);
      if (childNode) {
        const allChildrenChecked = category.children.every(child =>
          updatedPermissions.some(p => p.page_id.toString() === child.id)
        );
        if (allChildrenChecked) {
          await handlePermissionChange(category.id, 'view', true);
        }
        break;
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: 'primary.main',
              width: 56,
              height: 56,
            }}
          >
            <SecurityIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {t("authorization.managingUserAccessLevel")}
            </Typography>

          </Box>
        </Box>
        <Divider sx={{ my: 3 }} />
      </Box>

      {/* User Selection Card */}
      <Card
        elevation={2}
        sx={{
          mb: 4,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <PersonIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t("authorization.selectUser")}
            </Typography>
          </Box>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="user-select-label">
              {t("authorization.selectUser")}
            </InputLabel>
            <Select
              labelId="user-select-label"
              value={selectedUser ? selectedUser.id : ''}
              onChange={handleUserChange}
              label={t("authorization.selectUser")}
              sx={{
                bgcolor: 'background.paper',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.dark',
                },
              }}
            >
              <MenuItem value="">
                <em>{t("authorization.selectUser")}</em>
              </MenuItem>
              {users.map(user => (
                <MenuItem key={user.id} value={user.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                    <Typography>{user.username}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Permissions Section */}
      {selectedUser && (
        <Box>
          <Card
            elevation={0}
            sx={{
              mb: 2,
              borderRadius: 2,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              p: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 40, height: 40 }}>
                {selectedUser.username.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("authorization.userAccessLevel")}: {selectedUser.username}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {t("authorization.managePermissions") || "Manage user permissions by checking/unchecking items below"}
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Preset Selection Card */}
          <Card
            elevation={2}
            sx={{
              mb: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <SettingsIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("authorization.quickPresets") || "Brzo podešavanje nivoa pristupa"}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t("authorization.presetDescription") || "Izaberite preset za brzo podešavanje prava ili ručno podesite dole"}
              </Typography>

              {applyingPreset && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    {t("authorization.applyingPreset") || "Primenjivanje preseta..."}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {presets.map((preset) => (
                  <Button
                    key={preset.id}
                    variant={selectedPreset === preset.id ? "contained" : "outlined"}
                    onClick={() => {
                      applyPreset(preset.id);
                    }}
                    disabled={applyingPreset}
                    sx={{
                      minWidth: 140,
                      textTransform: 'none',
                      borderRadius: 2,
                      ...(selectedPreset === preset.id && {
                        bgcolor: 'primary.main',
                        '&:hover': {
                          bgcolor: 'primary.dark'
                        }
                      })
                    }}
                  >
                    {preset.label}
                  </Button>
                ))}
              </Box>
            </CardContent>
          </Card>

          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}
          >
            <CustomTreeView
              items={treeData}
              checkedItems={checkedItems}
              onCheck={handleTreeCheck}
            />
          </Paper>
        </Box>
      )}

      {/* Empty State */}
      {!selectedUser && (
        <Card
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: 'grey.50',
            borderRadius: 2,
            border: '2px dashed',
            borderColor: 'divider'
          }}
        >
          <PersonIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t("authorization.noUserSelected") || "No user selected"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("authorization.selectUserAbove") || "Please select a user from the dropdown above to manage their permissions"}
          </Typography>
        </Card>
      )}

      {/* Error and Success Messages */}
      <Box sx={{ mt: 3 }}>
        {errMsg && (
          <Alert
            severity="error"
            onClose={() => setErrMsg('')}
            sx={{ mb: 2, borderRadius: 2 }}
          >
            {errMsg}
          </Alert>
        )}
        {succ && (
          <Alert
            severity="success"
            onClose={() => setSucc('')}
            sx={{ mb: 2, borderRadius: 2 }}
          >
            {succ}
          </Alert>
        )}
      </Box>
    </Container>
  );
};

export default AccessRights;
